import { useState, useCallback, useMemo } from 'react';
import { CalendarViewMode, CalendarDayData } from '../calendarTypes';
import { getWeekDays, getWeekStart, formatDateStr, formatWeekRange } from '../utils/calendarUtils';

export function useCalendarNavigation() {
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [viewMode, setViewMode] = useState<CalendarViewMode>('week');

  const weekStart = useMemo(() => getWeekStart(currentDate), [currentDate]);
  const weekDays = useMemo(() => getWeekDays(currentDate), [currentDate]);
  const weekRangeLabel = useMemo(() => formatWeekRange(weekStart), [weekStart]);
  const currentDateStr = useMemo(() => formatDateStr(currentDate), [currentDate]);

  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const goNext = useCallback(() => {
    setCurrentDate(prev => {
      const d = new Date(prev);
      if (viewMode === 'week') {
        d.setDate(d.getDate() + 7);
      } else {
        d.setDate(d.getDate() + 1);
      }
      return d;
    });
  }, [viewMode]);

  const goPrev = useCallback(() => {
    setCurrentDate(prev => {
      const d = new Date(prev);
      if (viewMode === 'week') {
        d.setDate(d.getDate() - 7);
      } else {
        d.setDate(d.getDate() - 1);
      }
      return d;
    });
  }, [viewMode]);

  const selectDay = useCallback((day: CalendarDayData) => {
    setCurrentDate(day.date);
    setViewMode('day');
  }, []);

  const toggleViewMode = useCallback(() => {
    setViewMode(prev => (prev === 'week' ? 'day' : 'week'));
  }, []);

  return {
    currentDate,
    currentDateStr,
    viewMode,
    weekStart,
    weekDays,
    weekRangeLabel,
    setViewMode,
    goToToday,
    goNext,
    goPrev,
    selectDay,
    toggleViewMode,
  };
}
